// src/utils/firebase/fetchMeasurementVariantsPresence.js
import {
  collection,
  documentId,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";

function toRecordingIdCutoffPrefix(ms) {
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return null;

  const iso = d.toISOString();
  return iso.replace(/:(?=\d{2}[:.])/g, "-");
}

/**
 * Checks, for each variant, whether the user has a measurement doc.
 * When recentWithinMs is provided, the check becomes "has any doc id newer
 * than the cutoff timestamp?", using the timestamp-prefixed recording id.
 *
 * Assumed structure:
 *   measurements/{uid}/{variant}/{measurementId}
 *
 * @param {object} args
 * @param {import("firebase/firestore").Firestore} args.db
 * @param {string} args.uid
 * @param {string[]} args.variants
 * @param {boolean} [args.requireCompleted=false] - if true, filters for status == "completed"
 * @param {number|null} [args.recentWithinMs=null] - if set, require newest recording to be this recent
 * @returns {Promise<{ byVariant: Record<string, boolean>, any: boolean }>}
 */
export async function fetchMeasurementVariantsPresence({
  db,
  uid,
  variants,
  requireCompleted = false,
  recentWithinMs = null,
}) {
  if (!db) throw new Error("fetchMeasurementVariantsPresence: db is required");
  if (!uid)
    throw new Error("fetchMeasurementVariantsPresence: uid is required");
  if (!Array.isArray(variants) || variants.length === 0)
    throw new Error("fetchMeasurementVariantsPresence: variants[] is required");

  const checks = await Promise.all(
    variants.map(async (variant) => {
      const base = collection(db, "measurements", uid, variant);
      const shouldCheckRecency =
        typeof recentWithinMs === "number" &&
        Number.isFinite(recentWithinMs) &&
        recentWithinMs > 0;

      const constraints = [];
      if (requireCompleted) {
        constraints.push(where("status", "==", "completed"));
      }
      if (shouldCheckRecency) {
        const cutoffPrefix = toRecordingIdCutoffPrefix(
          Date.now() - recentWithinMs,
        );
        if (cutoffPrefix) {
          constraints.push(where(documentId(), ">=", cutoffPrefix));
        }
      }
      constraints.push(limit(1));

      const snap = await getDocs(query(base, ...constraints));
      return [variant, snap.size > 0];
    }),
  );

  const byVariant = Object.fromEntries(checks);
  const any = Object.values(byVariant).some(Boolean);

  return { byVariant, any };
}
